import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Stack,
  Button,
  Chip,
  LinearProgress,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// Material UI icons
import AutomationIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SavingsIcon from '@mui/icons-material/Savings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssessmentIcon from '@mui/icons-material/Assessment'; // Bu import zaten var ama kullanım hatası var
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import UpdateIcon from '@mui/icons-material/Update';

import AnimatedNumber from '../../components/common/AnimatedNumber';

// Renk paleti
const COLORS = ['#3f51b5', '#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#e91e63', '#00bcd4'];
const STATUS_COLORS = {
  completed: '#4caf50',
  inProgress: '#2196f3',
  queued: '#ff9800',
  error: '#f44336',
  pending: '#9c27b0'
};

// Örnek RPA süreç verileri
const generateProcessData = () => {
  // Süreç tipleri
  const processTypes = [
    { name: 'Veri Girişi', icon: <NoteAltIcon /> },
    { name: 'Form İşleme', icon: <FactCheckIcon /> },
    { name: 'Rapor Oluşturma', icon: <InsertChartIcon /> },
    { name: 'Veri Çıkarımı', icon: <TroubleshootIcon /> },
    { name: 'Dosya Taşıma', icon: <UpdateIcon /> },
    { name: 'Sistem Entegrasyonu', icon: <AccountTreeIcon /> }
  ];
  
  // Departmanlar
  const departments = ['Finans', 'İnsan Kaynakları', 'Satın Alma', 'Bilgi Teknolojileri', 'Müşteri Hizmetleri', 'Lojistik'];
  
  // Süreç durumları
  const statuses = ['completed', 'inProgress', 'queued', 'error', 'pending'];
  const statusDistribution = [0.6, 0.15, 0.1, 0.05, 0.1]; // Status dağılımı
  
  // Yaklaşık 25 süreç oluştur
  const processes = Array.from({ length: 25 }, (_, i) => {
    // Rastgele süreç tipi seç
    const processTypeIndex = Math.floor(Math.random() * processTypes.length);
    const processType = processTypes[processTypeIndex];
    
    // Rastgele departman seç
    const department = departments[Math.floor(Math.random() * departments.length)];
    
    // Durumu dağılıma göre seç
    let statusIndex = 0;
    const rand = Math.random();
    let cumulativeProbability = 0;
    
    for (let j = 0; j < statuses.length; j++) {
      cumulativeProbability += statusDistribution[j];
      if (rand <= cumulativeProbability) {
        statusIndex = j;
        break;
      }
    }
    
    const status = statuses[statusIndex];
    
    // Duruma göre ilerleme ve tarihler belirle
    let progress = 0;
    let startDate, completionDate, estimatedCompletionDate;
    const now = new Date();
    
    switch (status) {
      case 'completed':
        progress = 100;
        // Son 30 günde tamamlanmış rastgele bir gün
        startDate = new Date(now);
        startDate.setDate(now.getDate() - Math.floor(Math.random() * 30));
        completionDate = new Date(startDate);
        completionDate.setHours(completionDate.getHours() + Math.floor(Math.random() * 5) + 1);
        break;
        
      case 'inProgress':
        progress = Math.floor(10 + Math.random() * 80);
        // Son 2 günde başlamış
        startDate = new Date(now);
        startDate.setHours(now.getHours() - Math.floor(Math.random() * 48));
        estimatedCompletionDate = new Date(now);
        estimatedCompletionDate.setHours(now.getHours() + Math.floor(Math.random() * 24) + 1);
        break;
        
      case 'queued':
        progress = 0;
        estimatedCompletionDate = new Date(now);
        estimatedCompletionDate.setHours(now.getHours() + Math.floor(Math.random() * 72) + 24);
        break;
        
      case 'error':
        progress = Math.floor(10 + Math.random() * 90);
        startDate = new Date(now);
        startDate.setHours(now.getHours() - Math.floor(Math.random() * 24));
        break;
        
      case 'pending':
        progress = 0;
        break;
        
      default:
        progress = 0;
    }
    
    // Süreç sonuçları - tamamlanan süreçler için
    let results = {};
    if (status === 'completed') {
      results = {
        timeSaved: Math.floor(0.5 + Math.random() * 4.5), // 0.5-5 saat
        recordsProcessed: Math.floor(50 + Math.random() * 450), // 50-500 kayıt
        accuracy: 95 + Math.random() * 5, // %95-100
        costSavings: Math.floor(50 + Math.random() * 950) // 50-1000 TL
      };
    }
    
    // Bot türü
    const botType = Math.random() > 0.7 ? 'attended' : 'unattended';
    
    return {
      id: i + 1,
      name: `${processType.name} - ${department}`,
      type: processType.name,
      typeIcon: processType.icon,
      department,
      status,
      progress,
      startDate,
      completionDate,
      estimatedCompletionDate,
      results,
      botType,
      priority: Math.floor(Math.random() * 3) + 1, // 1-3 arası öncelik
      frequency: ['Günlük', 'Haftalık', 'Aylık', 'Çeyreklik'][Math.floor(Math.random() * 4)],
      automationLevel: Math.floor(70 + Math.random() * 30) // %70-100
    };
  });
  
  return processes;
};

// Özet metrikler
const calculateSummaryMetrics = (processes) => {
  const totalProcesses = processes.length;
  const completedProcesses = processes.filter(p => p.status === 'completed').length;
  const inProgressProcesses = processes.filter(p => p.status === 'inProgress').length;
  const errorProcesses = processes.filter(p => p.status === 'error').length;
  
  // Tamamlanmış süreçlerden toplam tasarrufları hesapla
  const completedDetails = processes.filter(p => p.status === 'completed' && p.results);
  
  const totalTimeSaved = completedDetails.reduce((sum, p) => sum + (p.results.timeSaved || 0), 0);
  const totalRecordsProcessed = completedDetails.reduce((sum, p) => sum + (p.results.recordsProcessed || 0), 0);
  const avgAccuracy = completedDetails.length > 0 
    ? completedDetails.reduce((sum, p) => sum + (p.results.accuracy || 0), 0) / completedDetails.length 
    : 0;
  const totalCostSavings = completedDetails.reduce((sum, p) => sum + (p.results.costSavings || 0), 0);
  
  // Departmanlara göre süreç dağılımı
  const departmentCounts = {};
  processes.forEach(p => {
    if (!departmentCounts[p.department]) {
      departmentCounts[p.department] = 0;
    }
    departmentCounts[p.department]++;
  });
  
  return {
    totalProcesses,
    completedProcesses,
    inProgressProcesses,
    errorProcesses,
    totalTimeSaved,
    totalRecordsProcessed,
    avgAccuracy,
    totalCostSavings,
    departmentCounts,
    automationRate: completedProcesses / totalProcesses * 100
  };
};

// Duruma göre ikon ve renk belirleme
const getStatusInfo = (status) => {
  switch (status) {
    case 'completed':
      return { icon: <CheckCircleIcon color="success" />, color: STATUS_COLORS.completed, label: 'Tamamlandı' };
    case 'inProgress':
      return { icon: <HourglassEmptyIcon color="primary" />, color: STATUS_COLORS.inProgress, label: 'İşlemde' };
    case 'queued':
      return { icon: <AccessTimeIcon color="warning" />, color: STATUS_COLORS.queued, label: 'Sırada' };
    case 'error':
      return { icon: <WarningIcon color="error" />, color: STATUS_COLORS.error, label: 'Hata' };
    case 'pending':
      return { icon: <UpdateIcon sx={{ color: '#9c27b0' }} />, color: STATUS_COLORS.pending, label: 'Beklemede' };
    default:
      return { icon: <AutomationIcon />, color: '#757575', label: 'Bilinmiyor' };
  }
};

// Bot türüne göre simge belirleme
const getBotTypeInfo = (botType) => {
  switch (botType) {
    case 'attended':
      return { icon: <DesktopWindowsIcon />, label: 'Etkileşimli Bot' };
    case 'unattended':
      return { icon: <AutoAwesomeIcon />, label: 'Tam Otonom Bot' };
    default:
      return { icon: <SmartphoneIcon />, label: 'Hibrit Bot' };
  }
};

// Süreç zamanlaması veri simülasyonu
const generateProcessTimeline = () => {
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Agu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const currentMonth = new Date().getMonth();
  
  const last12Months = Array.from({ length: 12 }, (_, i) => {
    const month = (currentMonth - 11 + i) % 12;
    return months[month >= 0 ? month : month + 12];
  });
  
  // Aylara göre otomatikleştirilen süreç sayıları
  return last12Months.map((month, index) => {
    // İlerleyen aylarda artan trend
    const baseValue = 5 + index * 2;
    // Rastgele varyasyon ekle
    const randomVariation = Math.random() * 5 - 2.5;
    
    return {
      month,
      automatedProcesses: Math.max(0, Math.round(baseValue + randomVariation)),
      manualProcesses: Math.max(0, Math.round(15 - index * 0.5 + Math.random() * 3 - 1.5))
    };
  });
};

// Departmanlara göre metrikler
const generateDepartmentMetrics = (processes) => {
  // Benzersiz departmanları al
  const departments = [...new Set(processes.map(p => p.department))];
  
  // Her departman için metrikleri hesapla
  return departments.map(department => {
    const deptProcesses = processes.filter(p => p.department === department);
    const completedProcesses = deptProcesses.filter(p => p.status === 'completed');
    
    const totalTime = completedProcesses.reduce((sum, p) => sum + (p.results?.timeSaved || 0), 0);
    const totalCost = completedProcesses.reduce((sum, p) => sum + (p.results?.costSavings || 0), 0);
    const processCount = deptProcesses.length;
    const automationRate = processCount > 0 ? (completedProcesses.length / processCount * 100) : 0;
    
    return {
      department,
      processCount,
      timeSaved: totalTime,
      costSavings: totalCost,
      automationRate
    };
  });
};

// Ana Bileşen
const RPA = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [processes, setProcesses] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedProcessType, setSelectedProcessType] = useState('all');
  const [processTimeline, setProcessTimeline] = useState([]);
  const [departmentMetrics, setDepartmentMetrics] = useState([]);
  
  // Veri yükleme simülasyonu
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const processesData = generateProcessData();
      setProcesses(processesData);
      
      const metrics = calculateSummaryMetrics(processesData);
      setSummaryMetrics(metrics);
      
      const timeline = generateProcessTimeline();
      setProcessTimeline(timeline);
      
      const deptMetrics = generateDepartmentMetrics(processesData);
      setDepartmentMetrics(deptMetrics);
      
      setLoading(false);
    }, 1500);
  }, []);
  
  // Sekme değişimi işleyicisi
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Filtre değişikliği işleyicisi
  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };
  
  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };
  
  const handleProcessTypeChange = (event) => {
    setSelectedProcessType(event.target.value);
  };
  
  // Filtreli süreçleri al
  const getFilteredProcesses = () => {
    return processes.filter(process => {
      const matchesStatus = selectedStatus === 'all' || process.status === selectedStatus;
      const matchesDepartment = selectedDepartment === 'all' || process.department === selectedDepartment;
      const matchesType = selectedProcessType === 'all' || process.type === selectedProcessType;
      return matchesStatus && matchesDepartment && matchesType;
    });
  };
  
  // Benzersiz departmanlar
  const uniqueDepartments = [...new Set(processes.map(p => p.department))];
  
  // Benzersiz süreç tipleri
  const uniqueProcessTypes = [...new Set(processes.map(p => p.type))];
  
  // Durum grafiği verisi
  const statusChartData = [
    { name: 'Tamamlandı', value: processes.filter(p => p.status === 'completed').length },
    { name: 'İşlemde', value: processes.filter(p => p.status === 'inProgress').length },
    { name: 'Sırada', value: processes.filter(p => p.status === 'queued').length },
    { name: 'Hata', value: processes.filter(p => p.status === 'error').length },
    { name: 'Beklemede', value: processes.filter(p => p.status === 'pending').length }
  ];

  // Para biçimlendirme
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Robotik Süreç Otomasyonu (RPA)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Tekrarlayan manuel işlemleri otomatikleştirin, iş süreçlerinizi optimize edin ve personel kaynaklarınızı daha değerli görevlere yönlendirin.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Yükleniyor durumu */}
      {loading && (
        <Box sx={{ width: '100%', my: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            RPA süreç verileri yükleniyor...
          </Typography>
        </Box>
      )}

      {!loading && (
        <>
          {/* Özet Kartlar */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Otomasyon Oranı
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber
                          value={Math.round(summaryMetrics.automationRate)}
                          suffix="%"
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summaryMetrics.completedProcesses} / {summaryMetrics.totalProcesses} süreç
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#3f51b5', width: 48, height: 48 }}>
                      <AutomationIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Tasarruf Edilen Zaman
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber
                          value={summaryMetrics.totalTimeSaved}
                          suffix=" saat"
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Son 30 günde
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                      <AccessTimeIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Maliyet Tasarrufu
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber
                          value={summaryMetrics.totalCostSavings}
                          formatter={(value) => formatCurrency(value)}
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Son 30 günde
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                      <SavingsIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ortalama Doğruluk
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber
                          value={summaryMetrics.avgAccuracy}
                          suffix="%"
                          decimals={1}
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summaryMetrics.totalRecordsProcessed} kayıt işlendi
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#f44336', width: 48, height: 48 }}>
                      <FactCheckIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Ana Sekmeler */}
          <Paper sx={{ borderRadius: 2, mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Süreç İzleme" 
                icon={<AnalyticsIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Departman Analizi" 
                icon={<AssessmentIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Otomatikleştirme Adımları" 
                icon={<BuildIcon />} 
                iconPosition="start" 
              />
            </Tabs>
          </Paper>

          {/* Süreç İzleme Sekmesi */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={9}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">RPA Süreç Durumu</Typography>
                    <Stack direction="row" spacing={2}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Durum</InputLabel>
                        <Select
                          value={selectedStatus}
                          label="Durum"
                          onChange={handleStatusChange}
                        >
                          <MenuItem value="all">Tüm Durumlar</MenuItem>
                          <MenuItem value="completed">Tamamlandı</MenuItem>
                          <MenuItem value="inProgress">İşlemde</MenuItem>
                          <MenuItem value="queued">Sırada</MenuItem>
                          <MenuItem value="error">Hata</MenuItem>
                          <MenuItem value="pending">Beklemede</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Departman</InputLabel>
                        <Select
                          value={selectedDepartment}
                          label="Departman"
                          onChange={handleDepartmentChange}
                        >
                          <MenuItem value="all">Tüm Departmanlar</MenuItem>
                          {uniqueDepartments.map(dept => (
                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Süreç Tipi</InputLabel>
                        <Select
                          value={selectedProcessType}
                          label="Süreç Tipi"
                          onChange={handleProcessTypeChange}
                        >
                          <MenuItem value="all">Tüm Süreçler</MenuItem>
                          {uniqueProcessTypes.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>

                  {/* Süreç Listesi */}
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <List sx={{ bgcolor: 'background.paper' }} disablePadding>
                      {getFilteredProcesses().slice(0, 8).map((process, index) => {
                        const statusInfo = getStatusInfo(process.status);
                        const botTypeInfo = getBotTypeInfo(process.botType);
                        
                        return (
                          <React.Fragment key={process.id}>
                            {index > 0 && <Divider />}
                            <ListItem 
                              sx={{ 
                                px: 3, 
                                py: 2,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' } 
                              }}
                              secondaryAction={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip 
                                    size="small"
                                    label={statusInfo.label}
                                    sx={{ 
                                      bgcolor: `${statusInfo.color}20`, 
                                      color: statusInfo.color,
                                      fontWeight: 500
                                    }}
                                    icon={
                                      <Box sx={{ color: statusInfo.color }}>
                                        {statusInfo.icon}
                                      </Box>
                                    }
                                  />
                                  <Tooltip title={botTypeInfo.label}>
                                    <IconButton edge="end" size="small">
                                      {botTypeInfo.icon}
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              }
                            >
                              <ListItemIcon>
                                {process.typeIcon}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" component="span">
                                      {process.name}
                                    </Typography>
                                    {process.priority === 1 && (
                                      <Chip 
                                        label="Yüksek Öncelik" 
                                        size="small" 
                                        color="error" 
                                        variant="outlined" 
                                        sx={{ ml: 1, height: 20 }} 
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <Typography variant="body2" component="span" color="text.secondary">
                                      {process.status === 'completed' && process.completionDate && 
                                        `Tamamlanma: ${process.completionDate.toLocaleString('tr-TR')}`
                                      }
                                      {process.status === 'inProgress' && process.startDate && 
                                        `Başlangıç: ${process.startDate.toLocaleString('tr-TR')}`
                                      }
                                      {process.status === 'queued' && process.estimatedCompletionDate && 
                                        `Tahmini Tamamlanma: ${process.estimatedCompletionDate.toLocaleString('tr-TR')}`
                                      }
                                    </Typography>
                                    {process.status === 'inProgress' && (
                                      <LinearProgress 
                                        variant="determinate" 
                                        value={process.progress}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Paper>
                </Paper>
              </Grid>

              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Durum Dağılımı</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Departman Analizi Sekmesi */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Departman Bazlı Süreç Metrikleri</Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={departmentMetrics}
                      margin={{
                        top: 5, right: 30, left: 20, bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="processCount" name="Süreç Sayısı" fill="#8884d8" />
                      <Bar dataKey="timeSaved" name="Tasarruf Edilen Zaman (saat)" fill="#82ca9d" />
                      <Bar dataKey="costSavings" name="Maliyet Tasarrufu (TL)" fill="#ffc658" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Otomatikleştirme Adımları Sekmesi */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Otomatikleştirme Adımları</Typography>
                  <Stepper orientation="vertical">
                    <Step active>
                      <StepLabel>Adım 1: Süreç Analizi</StepLabel>
                      <StepContent>
                        <Typography>İş süreçlerinizi analiz edin ve otomatikleştirilebilecek tekrarlayan görevleri belirleyin.</Typography>
                        <Button variant="contained" sx={{ mt: 2 }}>Devam Et</Button>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>Adım 2: RPA Araç Seçimi</StepLabel>
                      <StepContent>
                        <Typography>İhtiyaçlarınıza en uygun RPA araçlarını seçin ve entegrasyon planı oluşturun.</Typography>
                        <Button variant="contained" sx={{ mt: 2 }}>Devam Et</Button>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>Adım 3: Bot Geliştirme</StepLabel>
                      <StepContent>
                        <Typography>Seçilen RPA araçları ile botları geliştirin ve test edin.</Typography>
                        <Button variant="contained" sx={{ mt: 2 }}>Devam Et</Button>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>Adım 4: Uygulama ve İzleme</StepLabel>
                      <StepContent>
                        <Typography>Botları canlı ortama uygulayın ve performanslarını izleyin.</Typography>
                        <Button variant="contained" sx={{ mt: 2 }}>Devam Et</Button>
                      </StepContent>
                    </Step>
                  </Stepper>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default RPA;
